/* =========================================================
   REANA FOODS — PRODUCT PAGE BEHAVIOUR
   Used by: reana-product-main, reana-product-details, reana-product-videos

   - Image gallery (thumbnails ↔ main image)
   - Hover zoom: lens on the image + magnified pane beside it (desktop mouse)
   - Full-screen viewer: tap / click the image, pinch-to-zoom, double-tap, drag, swipe (touch)
   - Variant picker: price, compare-at price, stock, SKU/barcode, button state, image, URL
   - Quantity selector
   - Product description: removes links, moves embedded videos into the Product Videos section
   Re-initialises itself when a section is (re)loaded in the Theme Editor.
========================================================= */

(function () {
  'use strict';

  /* This file can be included by several sections; only define once. */
  if (window.ReanaProductPage) {
    window.ReanaProductPage.init(document);
    return;
  }

  var hoverQuery = window.matchMedia
    ? window.matchMedia('(hover: hover) and (pointer: fine)')
    : { matches: false };

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }


  /* =======================================================
     GALLERY + HOVER ZOOM
  ======================================================= */

  function Gallery(product) {
    this.product = product;
    this.root = qs('[data-rpp-gallery]', product);
    this.box = qs('[data-rpp-box]', product);
    this.img = qs('[data-rpp-main]', product);
    this.lens = qs('[data-rpp-lens]', product);
    this.pane = qs('[data-rpp-pane]', product);
    this.paneImg = qs('[data-rpp-pane-img]', product);
    this.thumbs = qs('[data-rpp-thumbs]', product);
    this.thumbButtons = qsa('[data-rpp-thumb]', product);
    this.zoomEnabled = product.getAttribute('data-zoom-enabled') !== 'false';
    this.lightboxEnabled = product.getAttribute('data-lightbox-enabled') !== 'false';
    this.zoom = parseFloat(product.getAttribute('data-zoom')) || 2.5;
    this.index = 0;
    this.aspect = 1;
    this.zoomSrc = '';
    this.geo = null;
    this.raf = 0;
    this.pointer = null;
    this.lightbox = null;

    if (!this.box || !this.img) return;

    this.syncFromThumb(0);
    this.bindThumbs();
    this.bindZoom();
    this.bindLightbox();
  }

  Gallery.prototype.images = function () {
    return this.thumbButtons.map(function (button) {
      return {
        src: button.getAttribute('data-src'),
        srcset: button.getAttribute('data-srcset'),
        zoom: button.getAttribute('data-zoom') || button.getAttribute('data-src'),
        alt: button.getAttribute('data-alt') || '',
        width: parseFloat(button.getAttribute('data-width')) || 0,
        height: parseFloat(button.getAttribute('data-height')) || 0,
        id: button.getAttribute('data-image-id')
      };
    });
  };

  Gallery.prototype.syncFromThumb = function (index) {
    var data = this.images()[index];
    if (!data) {
      this.aspect = (this.img.naturalWidth / this.img.naturalHeight) || 1;
      this.zoomSrc = this.img.currentSrc || this.img.src;
      return;
    }
    this.aspect = data.width && data.height ? data.width / data.height : 1;
    this.zoomSrc = data.zoom;
  };

  /* Show image #index in the main area (and highlight its thumbnail). */
  Gallery.prototype.select = function (index) {
    var images = this.images();
    var data = images[index];
    if (!data) return;

    this.index = index;
    this.thumbButtons.forEach(function (button, i) {
      var active = i === index;
      button.classList.toggle('active', active);
      button.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.img.src = data.src;
    if (data.srcset) this.img.srcset = data.srcset;
    this.img.alt = data.alt;
    this.syncFromThumb(index);
    this.hideZoom();
  };

  Gallery.prototype.selectByImageId = function (imageId) {
    if (!imageId) return;
    var images = this.images();
    for (var i = 0; i < images.length; i++) {
      if (String(images[i].id) === String(imageId)) {
        if (i !== this.index) this.select(i);
        return;
      }
    }
  };

  Gallery.prototype.bindThumbs = function () {
    var self = this;
    this.thumbButtons.forEach(function (button, i) {
      button.addEventListener('click', function () {
        self.select(i);
      });
    });
  };


  /* ---------- hover zoom ---------- */

  Gallery.prototype.bindZoom = function () {
    var self = this;
    if (!this.zoomEnabled || !this.pane || !this.paneImg || !this.lens) return;

    this.box.addEventListener('pointerenter', function (event) {
      if (event.pointerType !== 'mouse' || !hoverQuery.matches) return;
      self.showZoom(event);
    });

    this.box.addEventListener('pointermove', function (event) {
      if (event.pointerType !== 'mouse' || !self.geo) return;
      self.pointer = { x: event.clientX, y: event.clientY };
      if (!self.raf) {
        self.raf = window.requestAnimationFrame(function () {
          self.raf = 0;
          self.moveZoom();
        });
      }
    });

    this.box.addEventListener('pointerleave', function () {
      self.hideZoom();
    });

    window.addEventListener('resize', function () {
      self.hideZoom();
    });
  };

  Gallery.prototype.measure = function () {
    var rect = this.box.getBoundingClientRect();
    var bw = rect.width;
    var bh = rect.height;
    var ar = this.aspect || 1;
    var dw;
    var dh;

    /* size of the picture inside the box (object-fit: contain) */
    if (ar > bw / bh) {
      dw = bw;
      dh = bw / ar;
    } else {
      dh = bh;
      dw = bh * ar;
    }

    var space = document.documentElement.clientWidth - rect.right - 24 - 16;
    var side = space >= 300;
    var pw = side ? Math.min(bw, space) : bw;
    var ph = bh;

    this.geo = {
      rect: rect,
      bw: bw,
      bh: bh,
      dw: dw,
      dh: dh,
      ox: (bw - dw) / 2,
      oy: (bh - dh) / 2,
      side: side,
      pw: pw,
      ph: ph,
      lw: Math.min(pw / this.zoom, bw),
      lh: Math.min(ph / this.zoom, bh)
    };
  };

  Gallery.prototype.loadZoomImage = function () {
    var self = this;
    var src = this.zoomSrc;
    if (!src || this.paneImg.getAttribute('data-src') === src) return;

    this.paneImg.setAttribute('data-src', src);
    /* low-res placeholder first, swap to the large file as soon as it has loaded */
    this.paneImg.src = this.img.currentSrc || this.img.src;

    var loader = new Image();
    loader.onload = function () {
      if (self.paneImg.getAttribute('data-src') === src) self.paneImg.src = src;
    };
    loader.src = src;
  };

  Gallery.prototype.showZoom = function (event) {
    this.measure();
    var g = this.geo;

    this.loadZoomImage();

    this.paneImg.style.width = g.dw * this.zoom + 'px';
    this.paneImg.style.height = g.dh * this.zoom + 'px';

    if (g.side) {
      this.pane.classList.remove('is-inner');
      this.pane.style.width = g.pw + 'px';
      this.lens.style.width = g.lw + 'px';
      this.lens.style.height = g.lh + 'px';
      this.lens.classList.add('is-active');
    } else {
      this.pane.classList.add('is-inner');
      this.pane.style.width = '';
      this.lens.classList.remove('is-active');
    }

    this.pane.classList.add('is-active');
    this.pointer = { x: event.clientX, y: event.clientY };
    this.moveZoom();
  };

  Gallery.prototype.moveZoom = function () {
    var g = this.geo;
    if (!g || !this.pointer) return;

    var x = clamp(this.pointer.x - g.rect.left, 0, g.bw);
    var y = clamp(this.pointer.y - g.rect.top, 0, g.bh);
    var z = this.zoom;
    var left;
    var top;

    if (g.side) {
      /* lens follows the mouse, pane shows exactly what is inside the lens */
      var lx = clamp(x - g.lw / 2, 0, g.bw - g.lw);
      var ly = clamp(y - g.lh / 2, 0, g.bh - g.lh);
      this.lens.style.transform = 'translate3d(' + lx + 'px,' + ly + 'px,0)';
      left = (g.ox - lx) * z;
      top = (g.oy - ly) * z;
    } else {
      /* magnify in place: the point under the mouse stays under the mouse */
      left = x - (x - g.ox) * z;
      top = y - (y - g.oy) * z;
    }

    this.paneImg.style.transform = 'translate3d(' + left + 'px,' + top + 'px,0)';
  };

  Gallery.prototype.hideZoom = function () {
    this.geo = null;
    this.pointer = null;
    if (this.raf) {
      window.cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
    if (this.pane) this.pane.classList.remove('is-active');
    if (this.lens) this.lens.classList.remove('is-active');
  };


  /* ---------- full-screen viewer (touch friendly) ---------- */

  Gallery.prototype.bindLightbox = function () {
    var self = this;
    if (!this.lightboxEnabled || !this.img) return;

    this.box.addEventListener('click', function () {
      self.openLightbox();
    });

    this.box.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        self.openLightbox();
      }
    });
  };

  Gallery.prototype.openLightbox = function () {
    var template = qs('[data-rpp-lightbox-template]', this.product.parentNode);
    if (!template || this.lightbox) return;

    var self = this;
    var images = this.images();
    if (!images.length) return;

    this.hideZoom();
    this.lightbox = new Lightbox(template, images, this.index, function (lastIndex) {
      self.lightbox = null;
      if (lastIndex !== self.index) self.select(lastIndex);
      if (self.box && self.box.focus) {
        try { self.box.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    });
  };


  /* =======================================================
     LIGHTBOX — pinch / double-tap / drag / swipe / wheel
  ======================================================= */

  var MAX_SCALE = 5;

  function Lightbox(template, images, startIndex, onClose) {
    this.images = images;
    this.index = startIndex || 0;
    this.onClose = onClose;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.pointers = {};
    this.pointerCount = 0;
    this.pinch = null;
    this.pan = null;
    this.lastTap = null;

    this.el = template.content.firstElementChild.cloneNode(true);
    this.stage = qs('[data-lb-stage]', this.el);
    this.img = qs('[data-lb-img]', this.el);
    this.counter = qs('[data-lb-counter]', this.el);
    this.prevBtn = qs('[data-lb-prev]', this.el);
    this.nextBtn = qs('[data-lb-next]', this.el);

    if (images.length < 2) {
      this.prevBtn.hidden = true;
      this.nextBtn.hidden = true;
      this.counter.hidden = true;
    }

    document.body.appendChild(this.el);
    document.documentElement.classList.add('rpp-lb-open');
    this.opener = document.activeElement;

    this.bind();
    this.show(this.index);

    var closeButton = qs('[data-lb-close]', this.el);
    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  Lightbox.prototype.show = function (index) {
    var count = this.images.length;
    this.index = (index + count) % count;
    var data = this.images[this.index];

    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.img.classList.remove('is-animating');
    this.img.src = data.zoom || data.src;
    this.img.alt = data.alt;
    this.apply();

    if (this.counter) this.counter.textContent = (this.index + 1) + ' / ' + count;
  };

  Lightbox.prototype.apply = function () {
    this.img.style.transform =
      'translate3d(' + this.tx + 'px,' + this.ty + 'px,0) scale(' + this.scale + ')';
  };

  Lightbox.prototype.constrain = function () {
    var maxX = (this.stage.clientWidth * (this.scale - 1)) / 2;
    var maxY = (this.stage.clientHeight * (this.scale - 1)) / 2;
    this.tx = clamp(this.tx, -maxX, maxX);
    this.ty = clamp(this.ty, -maxY, maxY);
  };

  /* zoom to newScale keeping the content under (clientX, clientY) in place */
  Lightbox.prototype.zoomAt = function (newScale, clientX, clientY, animate) {
    var rect = this.stage.getBoundingClientRect();
    var px = clientX - (rect.left + rect.width / 2);
    var py = clientY - (rect.top + rect.height / 2);
    var cx = (px - this.tx) / this.scale;
    var cy = (py - this.ty) / this.scale;

    this.scale = clamp(newScale, 1, MAX_SCALE);
    this.tx = px - cx * this.scale;
    this.ty = py - cy * this.scale;

    if (this.scale <= 1.001) {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
    }

    this.constrain();
    this.animate(animate);
    this.apply();
  };

  Lightbox.prototype.animate = function (on) {
    var img = this.img;
    img.classList.toggle('is-animating', !!on);
    if (on) {
      window.clearTimeout(this.animTimer);
      this.animTimer = window.setTimeout(function () {
        img.classList.remove('is-animating');
      }, 260);
    }
  };

  Lightbox.prototype.centerPoint = function () {
    var rect = this.stage.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  };

  Lightbox.prototype.bind = function () {
    var self = this;
    var stage = this.stage;

    qs('[data-lb-close]', this.el).addEventListener('click', function () { self.close(); });
    qs('[data-lb-zoom-in]', this.el).addEventListener('click', function () {
      var c = self.centerPoint();
      self.zoomAt(self.scale * 1.6, c.x, c.y, true);
    });
    qs('[data-lb-zoom-out]', this.el).addEventListener('click', function () {
      var c = self.centerPoint();
      self.zoomAt(self.scale / 1.6, c.x, c.y, true);
    });
    this.prevBtn.addEventListener('click', function () { self.show(self.index - 1); });
    this.nextBtn.addEventListener('click', function () { self.show(self.index + 1); });

    stage.addEventListener('wheel', function (event) {
      event.preventDefault();
      var factor = event.deltaY < 0 ? 1.18 : 1 / 1.18;
      self.zoomAt(self.scale * factor, event.clientX, event.clientY, false);
    }, { passive: false });

    stage.addEventListener('pointerdown', function (event) { self.onDown(event); });
    stage.addEventListener('pointermove', function (event) { self.onMove(event); });
    stage.addEventListener('pointerup', function (event) { self.onUp(event); });
    stage.addEventListener('pointercancel', function (event) { self.onUp(event, true); });

    this.onKey = function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        self.close();
      } else if (event.key === 'ArrowLeft' && self.images.length > 1) {
        self.show(self.index - 1);
      } else if (event.key === 'ArrowRight' && self.images.length > 1) {
        self.show(self.index + 1);
      } else if (event.key === '+' || event.key === '=') {
        var c = self.centerPoint();
        self.zoomAt(self.scale * 1.4, c.x, c.y, true);
      } else if (event.key === '-') {
        var d = self.centerPoint();
        self.zoomAt(self.scale / 1.4, d.x, d.y, true);
      } else if (event.key === 'Tab') {
        self.trapFocus(event);
      }
    };
    document.addEventListener('keydown', this.onKey);
  };

  Lightbox.prototype.trapFocus = function (event) {
    var focusable = qsa('button:not([hidden]):not(:disabled)', this.el);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  Lightbox.prototype.onDown = function (event) {
    if (event.target.closest && event.target.closest('button')) return;

    try { this.stage.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
    this.animate(false);

    if (!this.pointers[event.pointerId]) this.pointerCount++;
    this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY };

    if (this.pointerCount === 2) {
      this.startPinch();
      this.pan = null;
    } else if (this.pointerCount === 1) {
      this.pan = {
        x: event.clientX,
        y: event.clientY,
        tx: this.tx,
        ty: this.ty,
        time: Date.now(),
        moved: false
      };
      this.stage.classList.add('is-panning');
    }
  };

  Lightbox.prototype.activePair = function () {
    var ids = Object.keys(this.pointers);
    return [this.pointers[ids[0]], this.pointers[ids[1]]];
  };

  Lightbox.prototype.startPinch = function () {
    var pair = this.activePair();
    var rect = this.stage.getBoundingClientRect();
    var midX = (pair[0].x + pair[1].x) / 2 - (rect.left + rect.width / 2);
    var midY = (pair[0].y + pair[1].y) / 2 - (rect.top + rect.height / 2);

    this.pinch = {
      dist: Math.max(1, Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y)),
      scale: this.scale,
      cx: (midX - this.tx) / this.scale,
      cy: (midY - this.ty) / this.scale
    };
  };

  Lightbox.prototype.onMove = function (event) {
    if (!this.pointers[event.pointerId]) return;
    this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY };

    if (this.pointerCount === 2 && this.pinch) {
      var pair = this.activePair();
      var rect = this.stage.getBoundingClientRect();
      var dist = Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y);
      var midX = (pair[0].x + pair[1].x) / 2 - (rect.left + rect.width / 2);
      var midY = (pair[0].y + pair[1].y) / 2 - (rect.top + rect.height / 2);

      this.scale = clamp(this.pinch.scale * (dist / this.pinch.dist), 1, MAX_SCALE);
      this.tx = midX - this.pinch.cx * this.scale;
      this.ty = midY - this.pinch.cy * this.scale;
      this.constrain();
      this.apply();
      return;
    }

    if (this.pointerCount === 1 && this.pan) {
      var dx = event.clientX - this.pan.x;
      var dy = event.clientY - this.pan.y;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) this.pan.moved = true;

      if (this.scale > 1) {
        this.tx = this.pan.tx + dx;
        this.ty = this.pan.ty + dy;
        this.constrain();
        this.apply();
      }
    }
  };

  Lightbox.prototype.onUp = function (event, cancelled) {
    if (!this.pointers[event.pointerId]) return;

    var pan = this.pan;
    delete this.pointers[event.pointerId];
    this.pointerCount = Math.max(0, this.pointerCount - 1);
    try { this.stage.releasePointerCapture(event.pointerId); } catch (e) { /* ignore */ }

    if (this.pinch && this.pointerCount < 2) {
      this.pinch = null;
      if (this.scale < 1.02) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.animate(true);
        this.apply();
      }
      /* continue panning with the finger that is still down */
      var ids = Object.keys(this.pointers);
      if (ids.length === 1) {
        var left = this.pointers[ids[0]];
        this.pan = { x: left.x, y: left.y, tx: this.tx, ty: this.ty, time: Date.now(), moved: true };
      } else {
        this.pan = null;
      }
      return;
    }

    if (this.pointerCount === 0) {
      this.stage.classList.remove('is-panning');
      this.pan = null;
      if (cancelled || !pan) return;

      var dx = event.clientX - pan.x;
      var dy = event.clientY - pan.y;

      /* swipe to the next / previous image when not zoomed in */
      if (this.scale === 1 && this.images.length > 1 &&
          Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.show(this.index + (dx < 0 ? 1 : -1));
        this.lastTap = null;
        return;
      }

      /* double-tap toggles zoom */
      if (!pan.moved && Date.now() - pan.time < 300) {
        var now = Date.now();
        var last = this.lastTap;
        if (last && now - last.time < 320 &&
            Math.hypot(event.clientX - last.x, event.clientY - last.y) < 40) {
          this.lastTap = null;
          this.zoomAt(this.scale > 1.05 ? 1 : 2.5, event.clientX, event.clientY, true);
        } else {
          this.lastTap = { time: now, x: event.clientX, y: event.clientY };
        }
      }
    }
  };

  Lightbox.prototype.close = function () {
    document.removeEventListener('keydown', this.onKey);
    document.documentElement.classList.remove('rpp-lb-open');
    window.clearTimeout(this.animTimer);
    if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
    if (typeof this.onClose === 'function') this.onClose(this.index);
  };


  /* =======================================================
     VARIANTS, PRICE, QUANTITY
  ======================================================= */

  function Variants(product, gallery) {
    this.product = product;
    this.root = product.parentNode;
    this.gallery = gallery;

    var json = qs('[data-rpp-product-json]', this.root);
    try {
      this.data = json ? JSON.parse(json.textContent) : null;
    } catch (e) {
      this.data = null;
    }
    this.variants = this.data && this.data.variants ? this.data.variants : [];

    this.bindQuantity();
    if (!this.variants.length) return;

    this.options = qsa('[data-rpp-option]', product);
    if (this.options.length) {
      this.bindOptions();
      this.markUnavailable();
    }
  }

  Variants.prototype.bindQuantity = function () {
    var root = this.product;
    var valueEl = qs('[data-rpp-qty-value]', root);
    var inputs = qsa('[data-rpp-qty-input]', root);
    var quantity = 1;

    function render() {
      if (valueEl) valueEl.textContent = quantity;
      inputs.forEach(function (input) { input.value = quantity; });
    }

    var minus = qs('[data-rpp-qty-minus]', root);
    var plus = qs('[data-rpp-qty-plus]', root);

    if (minus) {
      minus.addEventListener('click', function () {
        quantity = Math.max(1, quantity - 1);
        render();
      });
    }
    if (plus) {
      plus.addEventListener('click', function () {
        quantity = Math.min(999, quantity + 1);
        render();
      });
    }
  };

  Variants.prototype.selection = function () {
    return this.options.map(function (fieldset) {
      var input = qs('[data-rpp-option-input]', fieldset);
      if (!input) return null;
      if (input.tagName === 'SELECT') return input.value;
      var checked = qs('input[type="radio"]:checked', fieldset);
      return checked ? checked.value : null;
    });
  };

  Variants.prototype.find = function (selection) {
    for (var i = 0; i < this.variants.length; i++) {
      var variant = this.variants[i];
      var match = true;
      for (var j = 0; j < selection.length; j++) {
        if (variant.options[j] !== selection[j]) {
          match = false;
          break;
        }
      }
      if (match) return variant;
    }
    return null;
  };

  Variants.prototype.bindOptions = function () {
    var self = this;
    this.options.forEach(function (fieldset) {
      fieldset.addEventListener('change', function () {
        var selection = self.selection();
        var label = qs('[data-rpp-selected]', fieldset);
        if (label) {
          var idx = self.options.indexOf(fieldset);
          label.textContent = selection[idx] || '';
        }
        self.update(self.find(selection));
        self.markUnavailable();
      });
    });
  };

  /* strike through option values that have no purchasable variant with the other current choices */
  Variants.prototype.markUnavailable = function () {
    var self = this;
    var selection = this.selection();

    this.options.forEach(function (fieldset, optionIndex) {
      qsa('input[type="radio"]', fieldset).forEach(function (input) {
        var available = self.variants.some(function (variant) {
          if (!variant.available || variant.options[optionIndex] !== input.value) return false;
          return selection.every(function (value, j) {
            return j === optionIndex || variant.options[j] === value;
          });
        });
        input.classList.toggle('is-soldout', !available);
      });
    });
  };

  Variants.prototype.stockText = function (meta, variant) {
    var inLabel = meta.getAttribute('data-in-label') || 'In stock';
    var lowLabel = meta.getAttribute('data-low-label') || 'Only [count] left';
    var outLabel = meta.getAttribute('data-out-label') || 'Sold out';
    var threshold = parseInt(meta.getAttribute('data-threshold'), 10) || 10;
    var showCount = meta.getAttribute('data-show-count') === 'true';

    if (!variant || !variant.available) return { state: 'out', text: outLabel };

    if (variant.tracked && variant.inventory > 0 && variant.inventory <= threshold) {
      return { state: 'low', text: lowLabel.replace('[count]', variant.inventory) };
    }

    var text = inLabel;
    if (showCount && variant.tracked && variant.inventory > 0) {
      text += ' (' + variant.inventory + ')';
    }
    return { state: 'in', text: text };
  };

  Variants.prototype.update = function (variant) {
    var root = this.product;
    var buy = qs('[data-rpp-buy]', root);
    var addLabel = buy ? buy.getAttribute('data-add-label') : 'Add to Cart';
    var soldOutLabel = buy ? buy.getAttribute('data-soldout-label') : 'Sold out';
    var unavailableLabel = buy ? buy.getAttribute('data-unavailable-label') : 'Unavailable';

    /* variant id for both forms */
    qsa('[data-rpp-variant-id]', root).forEach(function (input) {
      input.value = variant ? variant.id : '';
    });

    /* buttons */
    var canBuy = !!(variant && variant.available);
    var atc = qs('[data-rpp-atc]', root);
    var atcLabel = qs('[data-rpp-atc-label]', root);
    var buyNow = qs('[data-rpp-buy-now]', root);

    if (atc) atc.disabled = !canBuy;
    if (buyNow) buyNow.disabled = !canBuy;
    if (atcLabel) {
      atcLabel.textContent = !variant ? unavailableLabel : (variant.available ? addLabel : soldOutLabel);
    }

    if (!variant) return;

    /* price */
    var price = qs('[data-rpp-price]', root);
    var compare = qs('[data-rpp-compare]', root);
    if (price) price.textContent = variant.price;
    if (compare) {
      var compareValue = qs('[data-rpp-compare-value]', compare) || compare;
      compareValue.textContent = variant.compare_at_price;
      compare.hidden = !variant.on_sale;
    }
    var saleBadge = qs('[data-rpp-sale-badge]', root);
    if (saleBadge) {
      var percent = parseInt(variant.discount_percent, 10) || 0;
      var badgeLabel = saleBadge.getAttribute('data-label') || '[percent]% OFF';
      saleBadge.textContent = badgeLabel.replace('[percent]', percent);
      saleBadge.hidden = !(variant.on_sale && percent > 0);
    }

    /* SKU / barcode */
    ['sku', 'barcode'].forEach(function (key) {
      var row = qs('[data-rpp-meta="' + key + '"]', root);
      if (!row) return;
      var value = qs('[data-rpp-meta-value]', row);
      if (value) value.textContent = variant[key] || '';
      row.hidden = !variant[key];
    });

    /* stock */
    var metaBlock = qs('[data-rpp-meta-block]', root);
    var stockRow = qs('[data-rpp-stock]', root);
    if (metaBlock && stockRow) {
      var stock = this.stockText(metaBlock, variant);
      stockRow.className = 'rpp-meta__row rpp-stock rpp-stock--' + stock.state;
      var stockText = qs('[data-rpp-stock-text]', stockRow);
      if (stockText) stockText.textContent = stock.text;
    }

    /* image */
    if (this.gallery) this.gallery.selectByImageId(variant.image_id);

    /* URL (so the chosen variant survives a refresh / share) */
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) { /* ignore */ }

    document.dispatchEvent(new CustomEvent('reana:variant:change', {
      detail: { variant: variant, product: root }
    }));
  };


  /* =======================================================
     PRODUCT DETAILS (description)
     - removes clickable links
     - moves videos embedded in the description into the Product Videos section
  ======================================================= */

  function initDetails(details) {
    var content = qs('[data-rpp-details-content]', details);
    if (!content) return;

    var removeLinks = details.getAttribute('data-remove-links') !== 'false';
    var moveVideos = details.getAttribute('data-move-videos') !== 'false';
    var videoGrid = qs('[data-rpp-video-grid]', document);

    if (moveVideos && videoGrid) {
      /* wrappers that held a moved video; only these may be cleaned up afterwards */
      var emptiedParents = [];
      var rememberParent = function (element) {
        var parent = element.parentElement;
        while (parent && parent !== content) {
          if (emptiedParents.indexOf(parent) === -1) emptiedParents.push(parent);
          parent = parent.parentElement;
        }
      };

      qsa('video, iframe, embed, object', content).forEach(function (element) {
        rememberParent(element);
        var wrapper = document.createElement('div');
        wrapper.className = 'rpp-video rpp-video-embedded';

        if (element.tagName.toLowerCase() === 'video') {
          element.controls = true;
          element.autoplay = false;
          element.muted = false;
          element.playsInline = true;
        }

        wrapper.appendChild(element);
        videoGrid.appendChild(wrapper);
      });

      /* old descriptions may contain plain links to MP4/WebM/MOV files */
      qsa('a[href]', content).forEach(function (link) {
        var href = link.href || '';
        if (!/\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(href)) return;

        var wrapper = document.createElement('div');
        wrapper.className = 'rpp-video rpp-video-linked';

        var video = document.createElement('video');
        video.src = href;
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata';

        wrapper.appendChild(video);
        videoGrid.appendChild(wrapper);
        rememberParent(link);
        link.remove();
      });

      /* drop only the wrappers a moved video left empty. Blank spacer
         paragraphs and other formatting typed in Shopify Admin are kept. */
      emptiedParents.forEach(function (element) {
        if (!element.isConnected) return;
        if (!element.textContent.trim() &&
            !element.querySelector('img, table, ul, ol, h1, h2, h3, h4, h5, h6, hr, video, iframe')) {
          element.remove();
        }
      });
    }

    if (removeLinks) {
      qsa('a', content).forEach(function (link) {
        link.replaceWith(document.createTextNode(link.textContent || ''));
      });
    }

    refreshVideoSection();
  }

  /* show the Product Videos section only when there is something in it */
  function refreshVideoSection() {
    var grid = qs('[data-rpp-video-grid]', document);
    if (!grid) return;
    var section = grid.closest('[data-rpp-video-section]');
    if (!section) return;
    section.hidden = !grid.querySelector('.rpp-video');
  }


  /* =======================================================
     INIT
  ======================================================= */

  function initProduct(product) {
    if (product.getAttribute('data-rpp-ready')) return;
    product.setAttribute('data-rpp-ready', 'true');

    var gallery = new Gallery(product);
    new Variants(product, gallery);
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;

    qsa('[data-rpp-product]', root).forEach(initProduct);
    if (root.matches && root.matches('[data-rpp-product]')) initProduct(root);

    qsa('[data-rpp-details]', root).forEach(function (details) {
      if (details.getAttribute('data-rpp-ready')) return;
      details.setAttribute('data-rpp-ready', 'true');
      initDetails(details);
    });
  }

  window.ReanaProductPage = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme Editor: a section was added / edited */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    if (qs('[data-rpp-details]', document)) {
      /* videos section may have been re-rendered; re-run so embedded videos are moved again */
      qsa('[data-rpp-details]', document).forEach(function (details) {
        details.removeAttribute('data-rpp-ready');
      });
      init(document);
    }
  });
})();
